import { Box, Button, keyframes, styled, Typography } from "@mui/material";
import { readSplashHint } from "../../utils/splashHint";
import { pendingReads } from "../../utils/pendingReads";
import { useEffect } from "react";
import {
  reportSplashStall,
  SplashStage,
  useSplashPhase,
} from "./loadingSplash.helpers";

/**
 * Loading splash screen shown while the app is initializing.
 * Matches the HTML inline splash for a seamless transition.
 *
 * When a per-org splash hint is present (written by OrgSite on a prior load),
 * paint the org's background instead of the mode default — the same hint the
 * index.html parse-time script reads, so the React splash matches the inline
 * one with no flash. Non-org pages (/admin etc.) key to undefined → unchanged.
 *
 * The spinner escalates on a timer (#1770) so a bad connection is never an
 * unexplained wait: "Still connecting…" at ~5s, an offline hint + reload at
 * ~15s. Timers only — no network probe; the copy is theme-neutral platform
 * chrome (keyed to the splash hint's mode when one exists).
 *
 * Reaching `stalled` also reports once to Sentry, tagged with `stage` (#1491)
 * and the oldest still-pending read (#1129) — a hung cold start throws
 * nothing, so this is its only trace.
 */
export const LoadingSplash = ({ stage }: { stage?: SplashStage }) => {
  const hint = readSplashHint(location.pathname);
  const phase = useSplashPhase();
  useEffect(() => {
    if (phase !== "stalled") return;
    reportSplashStall({
      stage,
      pathname: location.pathname,
      online: navigator.onLine,
      pendingReads: pendingReads(),
    });
  }, [phase, stage]);
  const hintText =
    hint &&
    (hint.mode === "dark" ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)");
  return (
    <SplashContainer sx={hint ? { backgroundColor: hint.bg } : undefined}>
      <Spinner />
      {phase !== "quiet" && (
        <SplashMessage
          role="status"
          sx={hintText ? { color: hintText } : undefined}
        >
          {phase === "still-connecting"
            ? "Still connecting…"
            : "This is taking longer than usual — check your connection."}
        </SplashMessage>
      )}
      {phase === "stalled" && (
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          Reload
        </Button>
      )}
    </SplashContainer>
  );
};

const rotate = keyframes`
  to { transform: rotate(360deg); }
`;

const SplashContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.mode === "dark" ? "#242424" : "#ffffff",
  zIndex: 9999,
}));

const SplashMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  fontSize: 14,
  color:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.75)"
      : "rgba(0,0,0,0.65)",
}));

const Spinner = styled(Box)({
  width: 48,
  height: 48,
  position: "relative",
  animation: `${rotate} 1.8s linear infinite`,
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    borderRadius: "50%",
  },
  "&::before": {
    width: 20,
    height: 20,
    background: "#09C9D5",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
  },
  "&::after": {
    width: 14,
    height: 14,
    background: "#FF518E",
    bottom: 4,
    right: 4,
  },
});
